import React, { useState, useEffect, useCallback } from 'react';
import { Select, message } from 'antd';
import PriorityBadge from './components/PriorityBadge';
import AgeBadge from './components/AgeBadge';
import { fetchSamples, updateSampleStatus, updateSamplePriority } from './api';
import { useFilters } from './FilterContext';
import { useMasters } from './MastersContext';

const MARKET_COLORS = {
  US: { bg: '#eff6ff', text: '#2563eb' },
  EU: { bg: '#f0fdf4', text: '#059669' },
  EP: { bg: '#f5f3ff', text: '#7c3aed' },
};

const TYPE_COLORS = {
  Routine:   { bg: '#e0f2fe', text: '#0369a1' },
  Stability: { bg: '#f5f3ff', text: '#7c3aed' },
  Support:   { bg: '#fffbeb', text: '#d97706' },
};

const TAG_STYLE = (bg, text) => ({
  display: 'inline-block',
  fontSize: 10,
  fontWeight: 600,
  padding: '1px 5px',
  borderRadius: 3,
  backgroundColor: bg,
  color: text,
  whiteSpace: 'nowrap',
});

const ACTION_BTN = (bg, color) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 26, height: 20, border: 'none', borderRadius: 3,
  backgroundColor: bg, color: color, fontSize: 11, cursor: 'pointer',
  padding: 0, transition: 'all 0.12s',
});

const COLUMN_DEFS = [
  { key: 'pending',   title: 'Pending',             borderColor: '#2563eb' },
  { key: 'running',   title: 'Running',             borderColor: '#d97706' },
  { key: 'urgent',    title: 'Urgent',              borderColor: '#ea580c' },
  { key: 'completed', title: 'Completed/Cancelled', borderColor: '#059669' },
];

function sortSamples(list, sort) {
  const arr = [...list];
  switch (sort) {
    case 'newest':  return arr.sort((a, b) => new Date(b.receiptDate) - new Date(a.receiptDate));
    case 'product': return arr.sort((a, b) => (a.productName || '').localeCompare(b.productName || ''));
    case 'market':  return arr.sort((a, b) => (a.market || '').localeCompare(b.market || ''));
    default:        return arr.sort((a, b) => new Date(a.receiptDate) - new Date(b.receiptDate));
  }
}

export default function Planning() {
  const { buildQueryParams } = useFilters();
  const { ageingBands } = useMasters();
  const [samples, setSamples]       = useState([]);   // filtered (for Pending/Running/Urgent cards)
  const [allSamples, setAllSamples] = useState([]);   // unfiltered full DB (for Completed col + counters)
  const [sort, setSort] = useState('oldest');
  const [urgentOnly, setUrgentOnly] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const params = buildQueryParams();
      const [filtered, allData] = await Promise.all([
        fetchSamples({ ...params, pageSize: 9999 }),
        fetchSamples({ pageSize: 9999 }),
      ]);
      setSamples(filtered.samples || []);
      setAllSamples(allData.samples || []);
    } catch { /* handled */ }
  }, [buildQueryParams]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleStatusChange = useCallback(async (id, newStatus) => {
    try {
      await updateSampleStatus(id, newStatus);
      message.success(`Status updated to ${newStatus}`);
      loadData();
    } catch { message.error('Failed to update status'); }
  }, [loadData]);

  const handlePriorityToggle = useCallback(async (sample) => {
    const next = sample.priority === 'Urgent' ? 'Normal' : 'Urgent';
    try {
      await updateSamplePriority(sample.id, next);
      loadData();
    } catch { message.error('Failed to update priority'); }
  }, [loadData]);

  // Cards: Pending/Running/Urgent use date-filtered samples
  const pending   = sortSamples(samples.filter(s => s.status === 'Pending'  && (urgentOnly ? s.priority === 'Urgent' : s.priority !== 'Urgent')), sort);
  const running   = sortSamples(samples.filter(s => s.status === 'Running'  && (urgentOnly ? s.priority === 'Urgent' : s.priority !== 'Urgent')), sort);
  const urgent    = sortSamples(samples.filter(s => (s.status === 'Pending' || s.status === 'Running') && s.priority === 'Urgent'), sort);
  // Completed/Cancelled: always pull from full unfiltered DB, latest 50 (wireframe spec)
  const completed = sortSamples(allSamples.filter(s => s.status === 'Completed' || s.status === 'Cancelled'), 'newest').slice(0, 50);

  // Column header counters always from full unfiltered DB (wireframe spec)
  const dbCounters = {
    pending:   allSamples.filter(s => s.status === 'Pending'  && s.priority !== 'Urgent').length,
    running:   allSamples.filter(s => s.status === 'Running'  && s.priority !== 'Urgent').length,
    urgent:    allSamples.filter(s => (s.status === 'Pending' || s.status === 'Running') && s.priority === 'Urgent').length,
    completed: allSamples.filter(s => s.status === 'Completed' || s.status === 'Cancelled').length,
  };

  const totalOpen   = allSamples.filter(s => s.status === 'Pending' || s.status === 'Running').length;
  const totalClosed = allSamples.filter(s => s.status === 'Completed' || s.status === 'Cancelled').length;

  const columns = { pending, running, urgent, completed };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Toolbar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #d1d5db',
        padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 10,
        flexShrink: 0,
      }}>
        <Select
          value={sort}
          onChange={setSort}
          size="small"
          style={{ width: 130 }}
          options={[
            { value: 'oldest',  label: 'Oldest first' },
            { value: 'newest',  label: 'Newest first' },
            { value: 'product', label: 'By Product' },
            { value: 'market',  label: 'By Market' },
          ]}
        />
        <div style={{ width: 1, height: 18, background: '#d1d5db' }} />
        {/* Urgent toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}>
          <div style={{ position: 'relative', width: 30, height: 16 }}>
            <input
              type="checkbox"
              checked={urgentOnly}
              onChange={e => setUrgentOnly(e.target.checked)}
              style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
            />
            <div style={{
              position: 'absolute', inset: 0, borderRadius: 8, cursor: 'pointer',
              background: urgentOnly ? '#ea580c' : '#d1d5db', transition: 'background 0.15s',
            }}>
              <div style={{
                position: 'absolute', width: 11, height: 11, background: '#fff',
                borderRadius: '50%', top: 2.5,
                left: urgentOnly ? 16 : 3,
                transition: 'left 0.15s',
                boxShadow: '0 1px 2px rgba(0,0,0,.25)',
              }} />
            </div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 600, color: urgentOnly ? '#ea580c' : '#6b7280' }}>
            ★ Urgent Only
          </span>
        </label>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: '#6b7280' }}>
          <strong style={{ color: '#1a3353', fontFamily: "'JetBrains Mono', monospace" }}>{totalOpen}</strong> open
          &nbsp;·&nbsp;
          <strong style={{ color: '#1a3353', fontFamily: "'JetBrains Mono', monospace" }}>{totalClosed}</strong> closed
        </span>
      </div>

      {/* Kanban Board */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, overflow: 'hidden' }}>
        {COLUMN_DEFS.map(col => {
          const list = columns[col.key] || [];
          return (
            <div key={col.key} style={{
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              borderRight: '1px solid #d1d5db',
            }}>
              {/* Column header — counter always reflects full DB total (wireframe spec) */}
              <div style={{
                padding: '8px 12px',
                borderBottom: `2px solid ${col.borderColor}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexShrink: 0, background: '#fff',
              }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1a3353' }}>{col.title}</span>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11, fontWeight: 700, color: '#4b5563',
                  background: '#f3f4f6', border: '1px solid #d1d5db',
                  padding: '1px 7px', borderRadius: 4,
                }}>
                  {dbCounters[col.key]}
                </span>
              </div>

              {/* Cards */}
              <div style={{
                flex: 1, overflowY: 'auto', padding: '6px 5px',
                display: 'flex', flexDirection: 'column', gap: 5,
                background: '#f7f8fa',
              }}>
                {list.map(sample => {
                  const isUrgent = sample.priority === 'Urgent';
                  const mc = MARKET_COLORS[sample.market] || { bg: '#f3f4f6', text: '#374151' };
                  const tc = TYPE_COLORS[sample.analysisType] || { bg: '#f3f4f6', text: '#374151' };

                  return (
                    <div key={sample.id} style={{
                      background: isUrgent ? '#fff7ed' : '#fff',
                      borderRadius: 5,
                      border: isUrgent ? '1px solid #fdba74' : '1px solid #e5e7eb',
                      borderLeft: `3px solid ${isUrgent ? '#ea580c' : 'transparent'}`,
                      padding: '5px 8px',
                      boxShadow: '0 1px 2px rgba(0,0,0,.05)',
                    }}>
                      {/* AR + Product + Star — all on one row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                        <span style={{
                          fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                          fontWeight: 700, color: '#1a3353', flexShrink: 0,
                        }}>
                          {sample.arNumber}
                        </span>
                        <span style={{
                          fontSize: 10, fontWeight: 600, color: '#111827',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                        }}>
                          {sample.productName}
                        </span>
                        <PriorityBadge priority={sample.priority} status={sample.status} onClick={() => handlePriorityToggle(sample)} />
                      </div>

                      {/* Tags + Age + Actions — single row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'nowrap', overflow: 'hidden' }}>
                        {sample.market       && <span style={TAG_STYLE(mc.bg, mc.text)}>{sample.market}</span>}
                        {sample.analysisType && <span style={TAG_STYLE(tc.bg, tc.text)}>{sample.analysisType}</span>}
                        {sample.duration     && <span style={TAG_STYLE('#f3f4f6', '#4b5563')}>{sample.duration}</span>}
                        <AgeBadge ageDays={sample.ageDays || 0} ageingBands={ageingBands} />
                        <div style={{ flex: 1 }} />
                        {sample.status === 'Pending' && (
                          <>
                            <button style={ACTION_BTN('#fffbeb', '#d97706')} title="Start" onClick={() => handleStatusChange(sample.id, 'Running')}>▶</button>
                            <button style={ACTION_BTN('#fef2f2', '#dc2626')} title="Cancel" onClick={() => handleStatusChange(sample.id, 'Cancelled')}>✕</button>
                          </>
                        )}
                        {sample.status === 'Running' && (
                          <>
                            <button style={ACTION_BTN('#f0fdf4', '#059669')} title="Complete" onClick={() => handleStatusChange(sample.id, 'Completed')}>✓</button>
                            <button style={ACTION_BTN('#fef2f2', '#dc2626')} title="Cancel" onClick={() => handleStatusChange(sample.id, 'Cancelled')}>✕</button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                {list.length === 0 && (
                  <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                    No samples
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
